import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button, Space, App, Alert } from 'antd';
import {
  CameraOutlined,
  SwapOutlined,
  CloseOutlined,
  CheckOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { withDevModalFilePath } from '../common';

interface CameraCaptureProps {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
}

const { Modal } = withDevModalFilePath('client/src/components/file/CameraCapture.tsx');

export const CameraCapture: React.FC<CameraCaptureProps> = ({
  open,
  onClose,
  onCapture,
}) => {
  const { message: messageApi } = App.useApp();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const capturedBlobRef = useRef<Blob | null>(null);

  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // カメラの起動
  const startCamera = useCallback(async () => {
    setIsLoading(true);
    setCameraError(null);

    try {
      // 既存のストリームを停止
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      // カメラの制約
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('カメラ起動エラー:', error);

      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          setCameraError('カメラへのアクセスが拒否されました。ブラウザの設定を確認してください。');
        } else if (error.name === 'NotFoundError') {
          setCameraError('カメラが見つかりません。デバイスにカメラが接続されているか確認してください。');
        } else {
          setCameraError('カメラの起動に失敗しました。');
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [facingMode]);

  // カメラの停止
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // カメラ切り替え
  const toggleCamera = () => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  };

  // 撮影
  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      messageApi.error('撮影に失敗しました');
      return;
    }

    ctx.drawImage(video, 0, 0);

    // Canvas を Blob に変換
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const imageUrl = URL.createObjectURL(blob);
          capturedBlobRef.current = blob;
          setCapturedImage(imageUrl);
          stopCamera();
        } else {
          messageApi.error('撮影に失敗しました');
        }
      },
      'image/jpeg',
      0.95
    );
  };

  // 再撮影
  const handleRetake = () => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
      setCapturedImage(null);
    }
    capturedBlobRef.current = null;
    startCamera();
  };

  // 使用する
  const handleUse = () => {
    if (!capturedImage || !capturedBlobRef.current) return;

    const blob = capturedBlobRef.current;
    const fileType = blob.type || 'image/jpeg';
    const file = new File([blob], `capture-${Date.now()}.jpg`, { type: fileType });

    onCapture(file);
    handleClose();
  };

  // モーダルを閉じる
  const handleClose = () => {
    stopCamera();
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
      setCapturedImage(null);
    }
    capturedBlobRef.current = null;
    setCameraError(null);
    onClose();
  };

  // モーダルが開いたときにカメラを起動
  useEffect(() => {
    if (open && !capturedImage) {
      startCamera();
    }

    return () => {
      stopCamera();
    };
  }, [capturedImage, open, startCamera, stopCamera]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (capturedImage) {
        URL.revokeObjectURL(capturedImage);
      }
    };
  }, [capturedImage]);

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        footer={null}
        width={800}
        centered
        destroyOnHidden
      >
        <div style={{ padding: '20px 0' }}>
        {cameraError ? (
          <Alert
            type="error"
            message="カメラエラー"
            description={cameraError}
            showIcon
            action={
              <Button size="small" onClick={handleClose}>
                閉じる
              </Button>
            }
          />
        ) : capturedImage ? (
          // プレビュー画面
          <div>
            <img
              src={capturedImage}
              alt="撮影した画像"
              style={{
                width: '100%',
                maxHeight: '60vh',
                objectFit: 'contain',
              }}
            />
            <Space style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}>
              <Button
                size="large"
                icon={<RedoOutlined />}
                onClick={handleRetake}
              >
                再撮影
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<CheckOutlined />}
                onClick={handleUse}
              >
                使用する
              </Button>
            </Space>
          </div>
        ) : (
          // カメラプレビュー画面
          <div>
            <div
              style={{
                position: 'relative',
                width: '100%',
                maxHeight: '60vh',
                backgroundColor: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  maxHeight: '60vh',
                  objectFit: 'contain',
                }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>

            <Space style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}>
              <Button
                icon={<SwapOutlined />}
                onClick={toggleCamera}
                disabled={isLoading}
              >
                カメラ切替
              </Button>
              <Button
                type="primary"
                size="large"
                shape="circle"
                icon={<CameraOutlined />}
                onClick={handleCapture}
                aria-label="撮影する"
                disabled={isLoading}
                style={{
                  width: 64,
                  height: 64,
                }}
              />
              <Button
                icon={<CloseOutlined />}
                onClick={handleClose}
              >
                キャンセル
              </Button>
            </Space>
          </div>
        )}
        </div>
      </Modal>
    </>
  );
};
